import React, { Fragment, useMemo } from "react";
import { Segment, Button, Divider, Icon } from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import { useParams, Link } from "react-router-dom";
import { useFormDefinitionById, useFormTypeById } from "../store/selectors/formSelectors";
import { useCurrentObservationById } from "../store/selectors/observationSelectors";

import ObservationDetail from "../components/observation/ObservationDetail";
import SchemaDisplay from "../components/SchemaDisplay";
import ProcessingForm from "../components/observation/processing/ProcessingForm";
import ErrorMessage from "../components/ErrorMessage";
import ProcessingDetail from "../components/observation/processing/ProcessingDetail";
import { EPermissionLevel } from "../interfaces/ICategoryPermission";
import { useLoggedInUser } from "../auth/store/authSelectors";
import { TFunction } from "i18next";
import { PageTemplate } from "../components/PageTemplate/PageTemplate";
import { useApplicationConfiguration } from "../configuration/ConfigurationContext";
import ObservationEditedDetail from "../components/observation/ObservationEditedDetail";
import { ErrorBoundary, isLoaded, isLoadErrored } from "../../../sg-core";
import { RenderPermissionForCategory } from "../components/permissions/RenderPermissionForCategory";
import { AnonymousObservationPopup } from "../components/observation/AnonymousObservationPopup";
import { getRouteHrefById } from "../../../routing";
import { HavainnotRouteId } from "../../routes";
import { useUserCanViewNames } from "../store/selectors/appRoleSelector";
import { useCategoryById } from "../store/selectors/categorySelectors";

const getTextProps = (t: TFunction) => ({
    worklistButtonText: t("Listalle"),
    authErrorText: t("Oikeuksia ei voitu tarkistaa"),
    loadErrorText: t("Latauksessa tapahtui virhe!"),
    editObservationText: t("Muokkaa havaintoa")
});

interface IObservationPage extends ReturnType<typeof getTextProps> { }

const ObservationPage: React.FC<IObservationPage> = ({ worklistButtonText, authErrorText, loadErrorText, editObservationText }) => {
    const { showDevelopmentLinks } = useApplicationConfiguration()
    const params = useParams<{ observation: string }>();
    const observationGuid = params.observation;

    const user = useLoggedInUser();
    const observation = useCurrentObservationById(observationGuid);

    const formTypeId = isLoaded(observation) ? observation.data.formType : undefined;
    const formType = useFormTypeById(formTypeId);
    const category = useCategoryById(formType?.category);
    const formDefinition = useFormDefinitionById(formType ? formType.formDefinition : undefined);
    const { allowEditingObservation } = useApplicationConfiguration()

    const displayFromData = isLoaded(observation) ? observation.data.formData : {};
    const formSchema = isLoaded(observation) && observation.data.formSchema ? observation.data.formSchema : undefined;

    const userIsEditor = Boolean(user && isLoaded(observation) && observation.data.editor && observation.data.editor === user.id)

    const canViewNames = useUserCanViewNames(formType?.category);


    return (
        <PageTemplate>

            <Button as={Link} size="small" icon to={getRouteHrefById(HavainnotRouteId.WorklistPage)}>
                <Icon name="angle left" />
                {worklistButtonText}
            </Button>


            {isLoaded(observation) && formSchema && formType ? (
                <>

                    <ObservationDetail observation={observation.data} showNameDetail={canViewNames} />

                    {showDevelopmentLinks && (
                        <Segment>
                            <AnonymousObservationPopup
                                observation={observation.data}
                                trigger={<a href="#"> Testaa anonyymiä prittaus näkymää </a>}
                            />
                        </Segment>
                    )}

                    <br />

                    <RenderPermissionForCategory
                        categoryId={formType.category}
                        minimumPermission={EPermissionLevel.OnlyPublic}
                        permissionType="edit"
                        render={({ permissionGranted: editorPermission }) => (
                            <Fragment>

                                {allowEditingObservation && editorPermission ? (
                                    <Fragment>
                                        <div>
                                            <Button
                                                as={Link}
                                                to={getRouteHrefById(HavainnotRouteId.EditObservationPage, { observation: observation.data.guid })}
                                                content={editObservationText}
                                            />
                                        </div>
                                        <br />
                                        <ErrorBoundary fallback={<div />}>
                                            <ObservationEditedDetail observation={observation.data} />
                                        </ErrorBoundary>
                                        <Divider hidden />
                                    </Fragment>
                                ) : null}


                                <SchemaDisplay formSchema={formSchema} formData={{ ...displayFromData }} canViewNames={canViewNames} />

                                <ProcessingDetail
                                    observation={observation.data}
                                    userIsEditor={editorPermission || userIsEditor}
                                />

                                {(editorPermission || userIsEditor) && category ? (
                                    <Fragment>
                                        <Divider />
                                        <ProcessingForm
                                            observation={observation.data}
                                            category={category}
                                        />
                                    </Fragment>
                                ) : null}
                            </Fragment>
                        )}
                    />


                </>
            ) : isLoadErrored(observation) || isLoadErrored(formDefinition) ? (
                <ErrorMessage
                    children={loadErrorText}
                    error={(isLoadErrored(observation) && observation.error) || (isLoadErrored(formDefinition) && formDefinition.error) || undefined}
                />
            ) : <Segment placeholder loading basic />}


        </PageTemplate>

    );
};

const MemoizedObservationPage = React.memo(ObservationPage);

const ObservationPageContainer: React.FC = () => {
    const { t } = useTranslation();
    const textProps = useMemo(() => getTextProps(t), [t]);
    return <MemoizedObservationPage {...textProps} />;
};

export default ObservationPageContainer;
